import chalk from "chalk";
import util from "util";










import { ConsoleTransport, ParentProcessTransport } from "./transports.js";


const ALL_LEVELS = [
    "silly",
    "debug",
    "logic",
    "info",
    "notice",
    "warn",
    "error",
    "results",
    "request",
    "response",
    "progress"
];
const DEFAULT_LEVELS = ALL_LEVELS.filter((l) => l !== "silly");

// Calculate maximum level name length for alignment
const MAX_LEVEL_LENGTH = Math.max(...ALL_LEVELS.map(level => level.toUpperCase().length));

const LEVEL_COLORS = {
    error: chalk.red.bold,
    warn: chalk.rgb(255, 165, 0),
    notice: chalk.cyan,
    info: chalk.white.bold,
    logic: chalk.gray,
    debug: chalk.gray,
    silly: chalk.gray,
    request: chalk.green,
    response: chalk.yellow,
    // Magenta stands out from white info / green request lines.
    progress: chalk.magentaBright,
    results: chalk.magenta
};

function paint(level, text, { color = true, bold = false } = {}) {
    if (!color || text == null || text === "") return text == null ? "" : String(text);
    const formatter = LEVEL_COLORS[level] ?? chalk.white;
    return bold ? formatter.bold(text) : formatter(text);
}

function inspectChunks(chunks, color = true) {
    return (chunks ?? [])
        .map((chunk) => util.inspect(chunk, { colors: color, depth: null }))
        .join(" ");
}

/**
 * Format a log struct for a terminal. Used by Logger and by UIs (tasksmm)
 * that persist structs and render them later.
 *
 * @param {{ level?: string, message?: string, name?: string, task?: string, prefix?: string, count?: unknown, total?: unknown,
 *           elapsed?: number, remaining?: number, rate?: number, chunks?: unknown[], results?: unknown }} struct
 * @param {{ timestamp?: boolean, showLevel?: boolean, color?: boolean, now?: Date|string|number }} [options]
 */
export function formatLog(struct = {}, options = {}) {
    const level = String(struct.level || "info").toLowerCase();
    const color = options.color !== false;
    const parts = [];
    const now = options.now != null ? new Date(options.now) : new Date();

    if (options.timestamp) {
        const d = Number.isNaN(now.getTime()) ? new Date() : now;
        parts.push(d.toISOString());
    }

    if (options.showLevel) {
        parts.push(level.toUpperCase().padEnd(MAX_LEVEL_LENGTH));
    }

    const taskName = struct.task != null ? String(struct.task).trim() : "";
    if (taskName) {
        parts.push(color ? chalk.cyan(`[${taskName}]`) : `[${taskName}]`);
    }

    if (level === "progress") {
        if (struct.prefix) {
            parts.push(paint(level, struct.prefix, { color, bold: true }));
        }
        if (struct.count !== undefined && struct.total !== undefined) {
            parts.push(paint(level, `${struct.count}/${struct.total}`, { color }));
        }
    } else if (struct.prefix) {
        parts.push(color ? chalk.cyan(`[${struct.prefix}]`) : `[${struct.prefix}]`);
    }

    if (struct.message) {
        parts.push(paint(level, struct.message, { color, bold: true }));
    }

    if (level === "progress") {
        if (struct.elapsed !== undefined && struct.remaining !== undefined) {
            parts.push(paint(level, `${struct.elapsed}/${struct.remaining}`, { color }));
        }
        if (struct.rate !== undefined) {
            parts.push(paint(level, Number(struct.rate) >= 0 ? `${struct.rate}/s` : "-/s", { color }));
        }
    }

    if (struct.chunks && struct.chunks.length) {
        parts.push(inspectChunks(struct.chunks, color));
    }

    if (struct.results) {
        parts.push(paint(level, JSON.stringify(struct.results, null, 4), { color }));
    }

    return parts.join(" ");
}

export class Logger  {
    context; // Partial context during initialization
    options;
    transport;
    startTimes = {};
    /** Count at the first progress sample for each prefix (rate baseline). */
    startCounts = {};
    lastProgressTimes = {};

    constructor(context, options = {}) {
        this.context = context;
        // Set defaults first
        this.options = this.getDefaultOptions();
        // Apply configuration
        if (options) {
            this.configure(options);
        }
        // Initialize transport
        this.updateTransport();
    }

    /**
     * Configure logger options. Accepts both LoggerOptions shape and flat param names
     * (levels string, progressWithTimes, progressWithRate, progressThrottleMs).
     */
    configure(options) {
        if (options.mode !== undefined) {
            this.options.mode = this.isValidMode(options.mode) ? options.mode : "text";
        }
        if (options.route !== undefined) {
            this.options.route = options.route;
            this.updateTransport();
        }
        if (options.prefix !== undefined) this.options.prefix = options.prefix;
        if (options.name !== undefined) this.options.name = options.name;
        if (options.logName !== undefined) this.options.name = options.logName;
        if (options.task !== undefined) this.options.task = options.task;
        if (options.silent !== undefined) this.options.silent = options.silent;
        if (options.showLevel !== undefined) this.options.showLevel = options.showLevel;
        if (options.timestamp !== undefined) this.options.timestamp = options.timestamp;
        if (options.levels !== undefined) {
            const levels = typeof options.levels === "string" ? options.levels.split(",") : options.levels;
            this.options.levels = this.normalizeLevels(levels);
        }
        if (options.progress !== undefined) {
            if (options.progress.withTimes !== undefined) this.options.progressTimes = options.progress.withTimes;
            if (options.progress.withRate !== undefined) this.options.progressRate = options.progress.withRate;
            if (options.progress.throttleMs !== undefined) this.options.progressThrottle = options.progress.throttleMs;
        }
        const flat = options ;
        if (flat.progressWithTimes !== undefined) this.options.progressTimes = flat.progressWithTimes;
        if (flat.progressWithRate !== undefined) this.options.progressRate = flat.progressWithRate;
        if (flat.progressThrottleMs !== undefined) this.options.progressThrottle = flat.progressThrottleMs;
    }

    /**
     * Initialize logger from context and CLI parameters. Whatever is in options goes (after discovered params).
     * Params are tracked under the `logger` module for --showUsedParams.
     */
    static init(context, options) {
        const paramDefs = {
            mode: "string default text",
            route: "string default console",
            prefix: "string",
            logName: "string",
            silent: "boolean default false",
            showLevel: "boolean default true",
            timestamp: "boolean default false",
            levels: "string",
            progressWithTimes: "boolean default false",
            progressWithRate: "boolean default false",
            progressThrottleMs: "number",
        };
        const discovered = context.params.getAllForModule("logger", paramDefs);
        const config = { ...discovered, ...options } ;
        if (!config.name && !config.logName) {
            const fromEnv = process.env.name || process.env.SERVICE_NAME;
            if (fromEnv) config.name = fromEnv;
        }
        const logger = new Logger(context, config);
        context.logger = logger;
        return logger;
    }

    getDefaultOptions() {
        return {
            mode: "text",
            route: this.shouldUseIpcRoute() ? "ipc" : "console",
            prefix: undefined,
            name: undefined,
            task: undefined,
            silent: false,
            showLevel: false,
            timestamp: false,
            levels: DEFAULT_LEVELS,
            progressTimes: false,
            progressRate: false,
            progressThrottle: undefined,
        };
    }

    updateTransport() {
        this.transport = this.options.route === "ipc"
            ? new ParentProcessTransport()
            : new ConsoleTransport();
    }

    /**
     * Same transport, tagged with a task name on every line. Does not mutate
     * this instance — safe when maxParallel > 1.
     *
     * @param {string} task
     * @returns {Logger}
     */
    child(task) {
        const next = Object.create(this);
        next.options = { ...this.options, task: task ? String(task) : this.options.task };
        return next;
    }

    setMode(mode) {
        if (!this.isValidMode(mode)) {
            throw new Error(`Unsupported logger mode: ${mode}`);
        }
        this.options.mode = mode;
    }

    /** Returns a styled string (bright white) for highlighting; keeps chalk inside logger. */
    highlight(text) {
        return chalk.whiteBright(text);
    }

    debug(message, ...chunks) {
        const out = this?.out;
        if (typeof out === "function") out.call(this, { level: "debug", message, chunks });
    }

    info(message, ...chunks) {
        const out = this?.out;
        if (typeof out === "function") out.call(this, { level: "info", message, chunks });
    }

    notice(message, ...chunks) {
        const out = this?.out;
        if (typeof out === "function") out.call(this, { level: "notice", message, chunks });
    }

    warn(message, ...chunks) {
        const out = this?.out;
        if (typeof out === "function") out.call(this, { level: "warn", message, chunks });
    }

    error(message, ...chunks) {
        const out = this?.out;
        if (typeof out === "function") out.call(this, { level: "error", message, chunks });
    }

    logic(message, ...chunks) {
        const out = this?.out;
        if (typeof out === "function") out.call(this, { level: "logic", message, chunks });
    }

    silly(message, ...chunks) {
        const out = this?.out;
        if (typeof out === "function") out.call(this, { level: "silly", message, chunks });
    }

    results(results) {
        const out = this?.out;
        if (typeof out === "function") out.call(this, { level: "results", message: "results", results });
    }

    request(operation, ...chunks) {
        const message = this.inspectChunks([operation, ...chunks]);
        this.out({ level: "request", message });
    }

    response(operation, ...chunks) {
        const message = this.inspectChunks([operation, ...chunks]);
        this.out({ level: "response", message });
    }

    /**
     * @returns {boolean} true when the progress line was emitted (not throttled)
     */
    progress(message, opts) {
        const { prefix, count, total } = opts;
        // If count briefly overshoots a stale total, display without negative ETA;
        // callers should raise `total` when the feed revises @odata.count.
        const displayTotal = Math.max(Number(total) || 0, Number(count) || 0);
        const paddedTotal = String(displayTotal).length;
        const paddedCount = String(count).padStart(paddedTotal, " ");
        const payload = {
            level: "progress",
            message,
            count: paddedCount,
            total: displayTotal,
            prefix
        };

        const key = prefix ?? "";
        // First sample for this prefix: establish baseline only (no rate yet).
        // Rate/ETA use items completed since that sample, so batched reporters
        // (e.g. every 500 records) don't inflate early /s from a late start clock.
        if (this.startTimes[key] === undefined) {
            this.startTimes[key] = Date.now();
            this.startCounts[key] = count;
        }
        const wantTimes = this.options.progressTimes;
        const wantRate = this.options.progressRate;
        if (wantTimes || wantRate) {
            const elapsedSeconds = (Date.now() - this.startTimes[key]) / 1000;
            const done = count - (this.startCounts[key] ?? count);
            const itemsPerSec = done > 0 && elapsedSeconds > 0 ? done / elapsedSeconds : -1;
            if (wantTimes) {
                let remaining = -1;
                if (itemsPerSec > 0) {
                    remaining = Math.max(0, (displayTotal - count) / itemsPerSec);
                }
                payload.elapsed = this.round(elapsedSeconds, 2);
                payload.remaining = remaining >= 0 ? this.round(remaining, 2) : remaining;
            }
            if (wantRate) {
                payload.rate = itemsPerSec >= 0 ? this.round(itemsPerSec, 2) : itemsPerSec;
            }
        }

        // Clear rate baseline on exact completion so the next job with the same
        // prefix re-baselines. Do not clear on overrun (count > total) — that
        // would zero the rate until total is corrected.
        if (count === total) {
            delete this.startTimes[key];
            delete this.startCounts[key];
            delete this.lastProgressTimes[key];
        }

        if (this.shouldOutputProgress(prefix ?? "", count, displayTotal)) {
            this.out(payload);
            if (this.options.progressThrottle && prefix) {
                this.lastProgressTimes[prefix] = Date.now();
            }
            return true;
        }
        return false;
    }

    shouldOutputProgress(prefix, count, total) {
        if (!this.options.progressThrottle) {
            return true;
        }
        if (count === 1 || count === total || !prefix) {
            return true;
        }
        const lastTime = this.lastProgressTimes[prefix];
        if (!lastTime) {
            return true;
        }
        return Date.now() - lastTime >= this.options.progressThrottle;
    }

    out(struct) {
        if (this.options.silent) {
            return;
        }
        if (!this.options.levels.includes(struct.level)) {
            return;
        }

        if (this.options.task && !struct.task) {
            struct.task = this.options.task;
        }
        if (this.options.prefix && !struct.prefix) {
            struct.prefix = this.options.prefix;
        }

        const output = this.options.mode === "json"
            ? JSON.stringify(struct)
            : this.formatLog(struct);

        this.transport.write(output);
    }

    formatLog(struct) {
        return formatLog(struct, {
            timestamp: this.options.timestamp,
            showLevel: this.options.showLevel,
            color: true,
        });
    }

    inspectChunks(chunks) {
        return inspectChunks(chunks, true);
    }

    shouldUseIpcRoute() {
        // Don't use IPC in test environments (Vitest workers interfere with process.send)
        if (process.env.VITEST || process.env.NODE_ENV === "test") {
            return false;
        }
        // Only use IPC if process.send exists AND we're connected to a parent
        // In Vitest workers, process.send exists but process.connected is undefined/false
        // process.connected is only true in actual child processes created with fork()
        return typeof process.send === "function" && process.connected === true;
    }

    normalizeLevels(levels) {
        if (!levels || !levels.length) {
            return DEFAULT_LEVELS;
        }

        const tokens = levels.map((t) => String(t).trim()).filter(Boolean);
        const explicitIncludes = tokens
            .filter((t) => !t.startsWith("+") && !t.startsWith("-"))
            .map((t) => t );
        const addIncludes = tokens
            .filter((t) => t.startsWith("+"))
            .map((t) => t.slice(1) );
        const excludes = tokens
            .filter((t) => t.startsWith("-"))
            .map((t) => t.slice(1) );

        const unknown = [...explicitIncludes, ...addIncludes, ...excludes].filter(level => !ALL_LEVELS.includes(level));
        if (unknown.length) {
            console.warn(`[Logger] Unknown level(s): ${unknown.join(", ")}`);
        }

        const base = explicitIncludes.length
            ? explicitIncludes
            : Array.from(new Set([...DEFAULT_LEVELS, ...addIncludes]));
        return base.filter(level => !excludes.includes(level));
    }

    isValidMode(mode) {
        return mode === undefined || mode === null || mode === "text" || mode === "json";
    }

    round(value, places) {
        const factor = Math.pow(10, places);
        return Math.round(value * factor) / factor;
    }
}

